import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// Track the currently open tooltip to close it when another opens
let currentOpenTooltip: { close: () => void } | null = null;

export interface TooltipProps {
  content?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: string;
  theme?: string;
}

export function Tooltip({
  content = '',
  position = 'top',
  maxWidth = '300px',
  theme: themeProp,
}: TooltipProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [isOpen, setIsOpen] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  // Keep the content hidden until it has been measured and positioned, so it
  // doesn't flash at the wrong spot for one frame on first open.
  const [isPositioned, setIsPositioned] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Keep a stable ref to the latest isOpen so event handlers always see current state
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const themeClass = useMemo(() => theme || '', [theme]);

  const positionTooltip = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 8;
    const padding = 12;

    let pos = position;

    // Smart positioning - flip if near edge
    if (pos === 'top' && triggerRect.top < tooltipRect.height + padding) {
      pos = 'bottom';
    } else if (pos === 'bottom' && triggerRect.bottom + tooltipRect.height + padding > viewportHeight) {
      pos = 'top';
    } else if (pos === 'left' && triggerRect.left < tooltipRect.width + padding) {
      pos = 'right';
    } else if (pos === 'right' && triggerRect.right + tooltipRect.width + padding > viewportWidth) {
      pos = 'left';
    }

    setActualPosition(pos);

    // Use fixed positioning with viewport coordinates to avoid overflow clipping
    const style: Record<string, string> = {
      position: 'fixed',
      maxWidth: maxWidth,
    };

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    switch (pos) {
      case 'top':
        style.bottom = (viewportHeight - triggerRect.top + gap) + 'px';
        style.left = triggerCenterX + 'px';
        style.transform = 'translateX(-50%)';
        break;
      case 'bottom':
        style.top = (triggerRect.bottom + gap) + 'px';
        style.left = triggerCenterX + 'px';
        style.transform = 'translateX(-50%)';
        break;
      case 'left':
        style.right = (viewportWidth - triggerRect.left + gap) + 'px';
        style.top = triggerCenterY + 'px';
        style.transform = 'translateY(-50%)';
        break;
      case 'right':
        style.left = (triggerRect.right + gap) + 'px';
        style.top = triggerCenterY + 'px';
        style.transform = 'translateY(-50%)';
        break;
    }

    setTooltipStyle(style as React.CSSProperties);
    setIsPositioned(true);

    // Prevent tooltip from going off-screen horizontally (check after render)
    requestAnimationFrame(() => {
      if (!tooltip) return;
      const updatedRect = tooltip.getBoundingClientRect();
      if (updatedRect.right > viewportWidth - padding) {
        setTooltipStyle({
          ...style,
          left: 'auto',
          right: padding + 'px',
          transform: 'none',
        } as React.CSSProperties);
      } else if (updatedRect.left < padding) {
        setTooltipStyle({
          ...style,
          left: padding + 'px',
          right: 'auto',
          transform: 'none',
        } as React.CSSProperties);
      }
    });
  }, [position, maxWidth]);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsPositioned(false);
    if (currentOpenTooltip?.close === close) {
      currentOpenTooltip = null;
    }
  }, []);

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    if (
      tooltip && !tooltip.contains(event.target as Node) &&
      trigger && !trigger.contains(event.target as Node)
    ) {
      close();
    }
  }, [close]);

  const handleKeydown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
      triggerRef.current?.focus();
    }
  }, [close]);

  const open = useCallback(() => {
    // Close any other open tooltip
    if (currentOpenTooltip && currentOpenTooltip.close !== close) {
      currentOpenTooltip.close();
    }
    currentOpenTooltip = { close };

    setIsOpen(true);
  }, [close]);

  const toggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (isOpenRef.current) {
      close();
    } else {
      open();
    }
  }, [close, open]);

  // Position tooltip after it becomes visible
  useEffect(() => {
    if (isOpen) {
      positionTooltip();
      document.addEventListener('click', handleOutsideClick);
      document.addEventListener('keydown', handleKeydown);
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen, positionTooltip, handleOutsideClick, handleKeydown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeydown);
      if (currentOpenTooltip?.close === close) {
        currentOpenTooltip = null;
      }
    };
  }, [handleOutsideClick, handleKeydown, close]);

  return (
    <span className={`kst-tooltip-wrapper ${themeClass}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`kst-tooltip-trigger${isOpen ? ' kst-tooltip-trigger--active' : ''}`}
        onClick={toggle}
        aria-expanded={isOpen}
        aria-label="More information"
      >
        <svg className="kst-tooltip-icon" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={tooltipRef}
          className={`kst-tooltip-content kst-tooltip-content--${actualPosition}`}
          style={{ ...tooltipStyle, visibility: isPositioned ? undefined : 'hidden' }}
          role="tooltip"
        >
          <div className="kst-tooltip-arrow"></div>
          <div
            className="kst-tooltip-text"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      )}
    </span>
  );
}

(window as any).KstTooltipReact = Tooltip;
